import { useInfiniteQuery } from 'react-query';

import {
  baseApiUrl,
  fetcher,
  ServerErrorResponse,
  createParamString,
  infiniteEmptyResults,
} from 'data_sources/constants';

import type { UserManualPermission } from 'types/permissions_response';
import type { ApiCollectionResponse } from 'types/api_response';

export default function useUserManualPermissions<T extends UserManualPermission>(
  organizationId: string,
  userId: string,
  params = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const url = `${baseApiUrl()}/lexid/user-permission/v1/organizations/${organizationId}/users/${userId}/manual-permissions`;
  return useInfiniteQuery<ApiCollectionResponse<T>, ServerErrorResponse>(
    ['user_manual_permissions', organizationId, userId, params],
    async ({ pageParam, signal }): Promise<ApiCollectionResponse<T>> => {
      try {
        const paramString = createParamString({
          size: 100,
          ...params,
        });
        const data: ApiCollectionResponse<T> = await fetcher({
          method: 'get',
          url: pageParam || `${url}?${paramString}`,
          options: { signal },
        });

        return data;
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage.offset && lastPage.offset < (lastPage.count || 0) && lastPage.content.length > 0) {
          const paramString = createParamString({
            ...params,
            offset: lastPage.offset,
          });
          return `${url}?${paramString}`;
        }
      },
      ...options,
    }
  );
}
